import React, { useState, useEffect } from 'react';

// Day / Month / Year dropdowns for date of birth.
//
// Why not <input type="date">? On Android the native picker opens a calendar
// that forces you to page back one month at a time — painful when your birth
// year is 30+ years ago. Three dropdowns let people jump straight to their year.
//
// value:    a 'YYYY-MM-DD' string (or '' when incomplete)
// onChange: called with the new 'YYYY-MM-DD' string, or '' if not fully filled.
//
// The three parts are held in LOCAL state so a partial selection (e.g. just the
// day) sticks on screen. We only push a value up to the parent once all three
// are chosen (otherwise ''). If we derived the parts straight from `value`, the
// first pick would report "incomplete" → value stays '' → the dropdown would
// snap back to blank, making it impossible to ever complete the date.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function daysInMonth(year, month1to12) {
  // month is 1-12; new Date(y, m, 0) gives the last day of month m.
  if (!year || !month1to12) return 31;
  return new Date(Number(year), Number(month1to12), 0).getDate();
}

function parse(value) {
  const [y = '', m = '', d = ''] = (value || '').split('-');
  // Strip leading zeros so the values match the numeric <option> values.
  return {
    y: y || '',
    m: m ? String(Number(m)) : '',
    d: d ? String(Number(d)) : '',
  };
}

export default function DobPicker({ value, onChange, style }) {
  const [parts, setParts] = useState(() => parse(value));

  // If the parent supplies a complete value that differs from what we're showing
  // (e.g. Profile loads the saved DOB after mount), sync it in.
  useEffect(() => {
    const next = parse(value);
    if (value && (next.y !== parts.y || next.m !== parts.m || next.d !== parts.d)) {
      setParts(next);
    }
  }, [value]); // eslint-disable-line

  const thisYear = new Date().getFullYear();
  // List newest years first so recent years are near the top of the dropdown.
  const years = [];
  for (let yr = thisYear; yr >= thisYear - 100; yr--) years.push(yr);

  const dayCount = daysInMonth(parts.y, parts.m);
  const days = [];
  for (let i = 1; i <= dayCount; i++) days.push(i);

  function change(part, raw) {
    const next = { ...parts, [part]: raw };

    // If the chosen day no longer exists in the new month/year, clamp it.
    if (next.y && next.m) {
      const max = daysInMonth(next.y, next.m);
      if (next.d && Number(next.d) > max) next.d = String(max);
    }

    setParts(next);

    if (next.y && next.m && next.d) {
      const mm = String(next.m).padStart(2, '0');
      const dd = String(next.d).padStart(2, '0');
      onChange(`${next.y}-${mm}-${dd}`);
    } else {
      onChange('');
    }
  }

  const selStyle = { flex: 1, minWidth: 0, ...style };

  return (
    <div style={{ display: 'flex', gap: 8 }}>
      <select
        className="form-input"
        value={parts.d}
        onChange={e => change('d', e.target.value)}
        style={selStyle}
        aria-label="Day of birth"
      >
        <option value="">Day</option>
        {days.map(n => <option key={n} value={n}>{n}</option>)}
      </select>
      <select
        className="form-input"
        value={parts.m}
        onChange={e => change('m', e.target.value)}
        style={{ ...selStyle, flex: 1.4 }}
        aria-label="Month of birth"
      >
        <option value="">Month</option>
        {MONTHS.map((name, i) => <option key={name} value={i + 1}>{name}</option>)}
      </select>
      <select
        className="form-input"
        value={parts.y}
        onChange={e => change('y', e.target.value)}
        style={selStyle}
        aria-label="Year of birth"
      >
        <option value="">Year</option>
        {years.map(yr => <option key={yr} value={yr}>{yr}</option>)}
      </select>
    </div>
  );
}
